import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import carBikeImage from "../../assets/car-bike.png";
import styles from "./AddYourVehicle.module.css";

/**
 * Vaahan Registration Component
 * Allows users to add their vaahan details and documents
 * Integrates with the RTOxpert API
 */
function AddYourVehicle() {
  const navigate = useNavigate();

  // State for form inputs
  const [vehicleData, setVehicleData] = useState({
    name: "",
    vehicle_number: "",
    phone: "",
    vehicle_type: "car", // Default value
    brand: "",
    model: "",
    year_of_registration: "",
    fuel_type: "petrol", // Default value
    chassis_number: "",
  });

  // State for file uploads
  const [files, setFiles] = useState([]);
  const [vehicleImage, setVehicleImage] = useState(null);

  // State for document types and expiry dates
  const [documentTypes, setDocumentTypes] = useState([]);
  const [expiryDates, setExpiryDates] = useState([]);

  // State for submission feedback
  const [showPopup, setShowPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVehicleData({
      ...vehicleData,
      [name]: value.toUpperCase(),
    });
  };

  // Handle document file changes
  const handleDocumentChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);

    // Initialize document types and expiry dates for each file
    const types = selectedFiles.map(() => "RC"); // Default to RC
    const dates = selectedFiles.map(() => ""); // Empty expiry dates

    setDocumentTypes(types);
    setExpiryDates(dates);
  };

  // Handle vehicle image upload
  const handleVehicleImageChange = (e) => {
    if (e.target.files[0]) {
      setVehicleImage(e.target.files[0]);
    }
  };

  // Handle document type change
  const handleDocTypeChange = (index, value) => {
    const newDocTypes = [...documentTypes];
    newDocTypes[index] = value;
    setDocumentTypes(newDocTypes);
  };

  // Handle expiry date change
  const handleExpiryDateChange = (index, value) => {
    const newExpiryDates = [...expiryDates];
    newExpiryDates[index] = value;
    setExpiryDates(newExpiryDates);
  };

  // Form submission handler
  const handleSubmit = async () => {
    // Reset error state
    setError("");

    // Get token from localStorage
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication token missing. Please log in again.");
      return;
    }

    // Validate required fields
    if (!vehicleData.vehicle_number || files.length === 0) {
      setError("Vaahan number and at least one document are required.");
      return;
    }

    // Show loading state
    setIsLoading(true);

    try {
      // Prepare request headers
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${token}`);

      // Create form data
      const formdata = new FormData();

      // Add vehicle data
      Object.entries(vehicleData).forEach(([key, value]) => {
        if (value) formdata.append(key, value);
      });

      // Add documents
      files.forEach((file, index) => {
        formdata.append("documents", file);
      });

      // Add document types as a comma-separated string
      formdata.append("document_types", documentTypes.join(","));

      // Add expiry dates as a comma-separated string
      formdata.append("expiry_dates", expiryDates.filter(date => date).join(","));

      // Add vehicle image if available
      if (vehicleImage) {
        formdata.append("vehicle_image", vehicleImage);
      }

      // API request options
      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow"
      };

      // Send request to API
      const response = await fetch("https://api.rtoxpert.com/api/vehicles", requestOptions);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to register vaahan");
      }

      // Store vehicle_id in localStorage
      if (result.vehicle_id) {
        localStorage.setItem("vehicle_id", result.vehicle_id);

        // You might want to store multiple vehicle IDs if the user has multiple vehicles
        const existingVehicles = JSON.parse(localStorage.getItem("user_vehicles") || "[]");
        existingVehicles.push({
          id: result.vehicle_id,
          number: vehicleData.vehicle_number,
          type: vehicleData.vehicle_type,
          brand: vehicleData.brand,
          model: vehicleData.model
        });
        localStorage.setItem("user_vehicles", JSON.stringify(existingVehicles));
      }

      // Show success message
      setShowPopup(true);
      window.location.href = "/garage";
      // Reset form after successful submission
      setVehicleData({
        name: "",
        vehicle_number: "",
        phone: "",
        vehicle_type: "car",
        brand: "",
        model: "",
        year_of_registration: "",
        fuel_type: "petrol",
        chassis_number: "",
      });
      setFiles([]);
      setVehicleImage(null);
      setDocumentTypes([]);
      setExpiryDates([]);

      // Hide success message after 5 seconds
      setTimeout(() => {
        setShowPopup(false);
      }, 5000);
    } catch (error) {
      console.error("Error:", error);
      setError(error.message || "Failed to register vaahan. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.formSection}>
        <div className={styles.titleContainer}>
          <h2 className={styles.formTitle}>Add Your Vaahan</h2>
          <button
            className={styles.searchButton}
            onClick={() => navigate("/searchDetails")}
          >
            Add vaahan via number
          </button>
        </div>

        {error && <div className={styles.errorMessage}>{error}</div>}

        <div className={styles.inputGroup}>
          <label>Name</label>
          <input
            type="text"
            name="name"
            value={vehicleData.name}
            onChange={handleInputChange}
            placeholder="Enter your name"
            className={styles.inputField}
          />
        </div>

        <div className={styles.inputGroup}>
          <label>Vaahan Number</label>
          <input
            type="text"
            name="vehicle_number"
            value={vehicleData.vehicle_number}
            onChange={handleInputChange}
            placeholder="E.g., MH01AB1234"
            className={styles.inputField}
          />
        </div>

        <div className={styles.inputGroup}>
          <label>Phone Number</label>
          <input
            type="text"
            name="phone"
            value={vehicleData.phone}
            onChange={handleInputChange}
            placeholder="Your contact number"
            className={styles.inputField}
          />
        </div>

        <div className={styles.inputRow}>
          <div className={styles.inputGroup}>
            <label>Vaahan Type</label>
            <select
              name="vehicle_type"
              value={vehicleData.vehicle_type}
              onChange={handleInputChange}
              className={styles.selectField}
            >
              <option value="car">Car</option>
              <option value="bike">Bike</option>
              <option value="truck">Truck</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className={styles.inputGroup}>
            <label>Fuel Type</label>
            <select
              name="fuel_type"
              value={vehicleData.fuel_type}
              onChange={handleInputChange}
              className={styles.selectField}
            >
              <option value="petrol">Petrol</option>
              <option value="diesel">Diesel</option>
              <option value="electric">Electric</option>
              <option value="cng">CNG</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
        </div>

        <div className={styles.inputRow}>
          <div className={styles.inputGroup}>
            <label>Brand</label>
            <input
              type="text"
              name="brand"
              value={vehicleData.brand}
              onChange={handleInputChange}
              placeholder="E.g., Toyota"
              className={styles.inputField}
            />
          </div>

          <div className={styles.inputGroup}>
            <label>Model</label>
            <input
              type="text"
              name="model"
              value={vehicleData.model}
              onChange={handleInputChange}
              placeholder="E.g., Camry"
              className={styles.inputField}
            />
          </div>
        </div>

        <div className={styles.inputRow}>
          <div className={styles.inputGroup}>
            <label>Year of Registration</label>
            <input
              type="text"
              name="year_of_registration"
              value={vehicleData.year_of_registration}
              onChange={handleInputChange}
              placeholder="E.g., 2021"
              className={styles.inputField}
            />
          </div>

          <div className={styles.inputGroup}>
            <label>Chassis Number</label>
            <input
              type="text"
              name="chassis_number"
              value={vehicleData.chassis_number}
              onChange={handleInputChange}
              placeholder="E.g., ABCXYZ123456"
              className={styles.inputField}
            />
          </div>
        </div>

        <div className={styles.fileInputGroup}>
          <label>Upload Documents (e.g. RC, Insurance)</label>
          <div className={styles.fileUploadWrapper}>
            <label className={styles.fileUploadLabel}>
              Choose Document Files
              <input
                type="file"
                className={styles.fileInput}
                onChange={handleDocumentChange}
                multiple
              />
            </label>
          </div>

          {files.length > 0 && (
            <div className={styles.uploadedFilesContainer}>
              {files.map((file, index) => (
                <div key={index} className={styles.fileItem}>
                  <div className={styles.fileDetails}>
                    <span className={styles.fileName}>📄 {file.name}</span>
                    <div className={styles.fileControls}>
                      <select
                        value={documentTypes[index]}
                        onChange={(e) => handleDocTypeChange(index, e.target.value)}
                        className={styles.docTypeSelect}
                      >
                        <option value="RC">RC</option>
                        <option value="Insurance">Insurance</option>
                        <option value="PUC">PUC</option>
                        <option value="Other">Other</option>
                      </select>

                      <input
                        type="date"
                        value={expiryDates[index]}
                        onChange={(e) => handleExpiryDateChange(index, e.target.value)}
                        placeholder="Expiry Date"
                        className={styles.expiryInput}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.fileInputGroup}>
          <label>Upload Vaahan Image</label>
          <div className={styles.fileUploadWrapper}>
            <label className={styles.fileUploadLabel}>
              Choose Vaahan Image
              <input
                type="file"
                className={styles.fileInput}
                onChange={handleVehicleImageChange}
                accept="image/*"
              />
            </label>
          </div>

          {vehicleImage && (
            <div className={styles.fileItem}>
              <span className={styles.fileName}>🖼️ {vehicleImage.name}</span>
            </div>
          )}
        </div>

        <button
          className={styles.submitButton}
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? "Submitting..." : "Submit"}
        </button>

        {showPopup && (
          <div className={styles.successMessage}>
            Vaahan registered successfully! Agent will reach out to you.
          </div>
        )}
      </div>

      <div className={styles.imageSection}>
        <img
          src={carBikeImage}
          alt="Vaahan images showing SUV and motorcycle"
          className={styles.vehicleImage}
        />
      </div>
    </div>
  );
}

export default AddYourVehicle;